/**
 * usePageHelp(pageKey): a page's help content (cached per page) plus its registry entry.
 * A 404 from the API means no help has been written yet: `page` is null and `notWritten` is true.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHelpService, type HelpPageView, type HelpTabView } from '@actschurch/ccms-sdk';
import { useHelpContext } from './HelpContext';
import { getHelpPageDef } from './helpRegistry';
import type { HelpPageDef, HelpTabDef } from './helpRegistryTypes';
import { HELP_STALE_MS, helpKeys } from './helpUtils';

export interface PageHelpResult {
  page: HelpPageView | null;
  def: HelpPageDef | undefined;
  isLoading: boolean;
  error: unknown;
  /** Loaded and no help is stored for this page yet. */
  notWritten: boolean;
  /** Title to show: stored, then registry, then the key. */
  title: string;
  refetch: () => void;
}

export function usePageHelp(pageKey: string | null | undefined): PageHelpResult {
  const ctx = useHelpContext();
  const help = useHelpService();
  const def = pageKey ? getHelpPageDef(pageKey) : undefined;

  const q = useQuery({
    queryKey: helpKeys.page(pageKey ?? ''),
    queryFn: () => help.getHelpPage(pageKey as string),
    enabled: !!pageKey && !!ctx?.ready,
    staleTime: HELP_STALE_MS,
    retry: 1,
  });

  const page = q.data ?? null;
  return {
    page,
    def,
    isLoading: q.isLoading && !!pageKey && !!ctx?.ready,
    error: q.error,
    notWritten: q.isSuccess && q.data === null,
    title: page?.title || def?.title || pageKey || 'Help',
    refetch: () => void q.refetch(),
  };
}

/** A tab's stored help and registry entry. */
export function findTabHelp(page: HelpPageView | null, def: HelpPageDef | undefined, tabKey: string | null | undefined): { tab: HelpTabView | undefined; tabDef: HelpTabDef | undefined } {
  if (!tabKey) return { tab: undefined, tabDef: undefined };
  return {
    tab: page?.tabs?.find((t) => t.tabKey === tabKey),
    tabDef: def?.tabs.find((t) => t.key === tabKey),
  };
}

/**
 * Tells the help framework which tab is open on a page, so "Ask AI" sends it and the help panel
 * opens at it. Call it in the page with its current tab key (null = no tab).
 */
export function useHelpActiveTab(pageKey: string, tabKey: string | null | undefined): void {
  const ctx = useHelpContext();
  const setActiveTab = ctx?.setActiveTab;
  useEffect(() => {
    if (!setActiveTab) return undefined;
    setActiveTab(pageKey, tabKey ?? null);
    return () => setActiveTab(pageKey, null);
  }, [setActiveTab, pageKey, tabKey]);
}
